import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { BoatListings } from './BoatListings';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { textFieldValidation } from '@/utils/validations';
import { useCreateBoatMutation } from '@/services/BoatManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import { BoatPayload, IBoatData } from '@/types/boat-management.types';
import { BoatModal } from './BoatModal';
import { usePermissions } from '@/hooks/usePermissions';

// Updated validation schema for create boat
const boatValidationSchema = Yup.object().shape({
  name: textFieldValidation('Boat Name', 2, 50, true),
  // model: textFieldValidation("Boat Model", 2, 50, true),
  type: Yup.string().required('Boat type is required'),
  length: Yup.string()
    .required('Length is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Length must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  width: Yup.string()
    .required('Width is required')
    .matches(
      /^\d+(?:[.,]\d{1,2})?$/,
      "Width must be a number with up to 2 decimals, and only one of ',' or '.' allowed"
    ),
  draft: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Draft must be a valid number')
    .nullable(),
  height: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Height must be a valid number')
    .nullable(),
  weight: Yup.string()
    .matches(/^[0-9]+([,.][0-9]+)?$/, 'Weight must be a valid number')
    .nullable(),
  registrationNumber: textFieldValidation('Registration Number', 3, 20, true),
  boaterId: Yup.string().required('Boater is required'),
  unit: Yup.string().oneOf(['m', 'ft']).required('Unit is required'),
});

const normalizeNumericFieldValue = (value: string) =>
  value.trim().replace(/\s/g, '').replace(/,/g, '.');

const BoatManagement = () => {
  const { currentLayout } = useLayout();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<IBoatData>>({
    name: '',
    type: 'Sailboat',
    model: '',
    length: '',
    width: '',
    draft: '',
    height: '',
    weight: '',
    registrationNumber: '',
    boater: { id: '', name: '', email: '' },
    status: 'Active',
    unit: 'ft',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { enqueueSnackbar } = useSnackbar();
  const [createBoat, { isLoading: isCreating }] = useCreateBoatMutation();
  const { hasPermission } = usePermissions();

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setFormData({
      name: '',
      type: 'Sailboat',
      model: '',
      length: '',
      width: '',
      draft: '',
      height: '',
      weight: '',
      registrationNumber: '',
      boater: { id: '', name: '', email: '' },
      status: 'Active',
      isCustomBoatModel: false,
      unit: 'ft',
    });
    setErrors({});
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({});
    setErrors({});
  };
  const handleSubmit = async (isCustomModel: boolean, boatModelId?: string) => {
    try {
      await boatValidationSchema.validate(
        {
          name: formData.name,
          model: formData.model,
          type: formData.type,
          length: formData.length,
          width: formData.width,
          draft: formData.draft,
          height: formData.height,
          weight: formData.weight,
          registrationNumber: formData.registrationNumber,
          boaterId: formData.boater?.id,
          unit: formData.unit,
        },
        { abortEarly: false }
      );

      const payload: BoatPayload = {
        name: formData.name || '',
        type: formData.type || '',
        length: normalizeNumericFieldValue(formData.length || ''),
        width: normalizeNumericFieldValue(formData.width || ''),
        draft: normalizeNumericFieldValue(formData.draft || ''),
        height: normalizeNumericFieldValue(formData.height || ''),
        weight: normalizeNumericFieldValue(formData.weight || ''),
        registration_number: formData.registrationNumber || '',
        boater_id: formData.boater?.id || '',
        is_custom_boat_model: isCustomModel,
        boat_model_name: formData.model || '',
        unit: formData.unit || 'ft',
        ...(boatModelId && !isCustomModel && { boat_model_id: boatModelId }),
      };
      await createBoat(payload).unwrap();
      enqueueSnackbar('Boat created successfully', { variant: 'success' });
      handleCloseCreateModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else if (err) {
        const errorMessage = err
          ? (err as ErrorApiResponse).data.message
          : 'An error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="boat-management-heading" />
              <ToolbarDescription>
                You can manage your boat management here.
              </ToolbarDescription>
            </ToolbarHeading>
            {hasPermission('boat_management_create') && (
              <ToolbarActions>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={handleOpenCreateModal}
                  aria-label="Add new boat"
                  title="Add new boat"
                >
                  Add New Boat
                </button>
              </ToolbarActions>
            )}
          </Toolbar>
        </Container>
      )}
      <Container>
        <BoatListings />
      </Container>
      <BoatModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        mode="create"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isCreating}
      />
    </Fragment>
  );
};

export { BoatManagement };
